import { motion } from 'motion/react';
import { memo } from 'react';
import { CategoryCardBaseProps } from './types';
import { PortableText } from '@/app/components/questionnaireV2/components/portable-text/portable-text';

export const CategoryCardBase = memo(function CategoryCardBase({
  name,
  description,
  icon,
  progressValue,
  progressMax,
  lastUpdated,
  isComplete,
  children,
}: CategoryCardBaseProps) {
  const progressPercentage = (progressValue / progressMax) * 100;

  return (
    <div className="dashboard-reset bg-[#f0f5f6] box-border flex flex-col rounded-[10px] w-full gap-[9px] items-start pb-[35px] pt-0 px-0 max-w-sm lg:max-w-none">
      {/* Header with Icon and Status Tags */}
      <div className="box-border flex h-[60px] items-center justify-between pl-0 pr-[20px] py-0 w-full">
        {/* Icon Container */}
        <div className="bg-white box-border flex items-center justify-center p-[10px] rounded-br-[10px] rounded-tl-[10px] size-[60px] border border-[#d4c2b5] border-solid">
          <div className="flex h-[30px] w-[30px] items-center justify-center">
            <img alt={`${name} icon`} className="object-contain" src={icon} />
          </div>
        </div>

        {/* Status Tags */}
        <div className="flex gap-[5px] items-center">
          {/* Last Updated Tag with conditional styling */}
          {(lastUpdated || !isComplete) && (
            <div className={`box-border flex gap-[2px] h-[25px] items-center justify-center px-[10px] rounded-[4px] ${
              isComplete ? 'bg-white' : 'bg-[#f5e695]'
            }`}>
              <p className="font-semibold text-[#183344] text-[12px] text-center whitespace-nowrap">
                {!isComplete ? `Last Updated ${lastUpdated} - Incomplete` : `Last Updated - ${lastUpdated}`}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Body Content */}
      <div className="box-border flex flex-col items-start lg:items-center w-full gap-[10px] lg:gap-[14px] px-[20px] lg:px-[64px] py-[5px] lg:py-0 lg:bg-gradient-to-r lg:from-[#f0f5f6] lg:to-[#f0f5f6] lg:rounded-[10px] flex-1">
        {/* Title and Progress Section */}
        <div className="flex flex-col w-full gap-[7px] items-start">
          {/* Title */}
          <h3 className="font-bold text-[#183344] text-[22px] w-full leading-[1.3]">
            {name}
          </h3>

          {/* Progress Section */}
          <div className="flex flex-col items-start w-full gap-[4px]">
            {/* Progress Bar */}
            <div className="w-full lg:w-72" role="progressbar" aria-valuenow={progressValue} aria-valuemin={0} aria-valuemax={progressMax} aria-label={`Progress: ${progressValue} of ${progressMax} factors analyzed`} aria-valuetext={`${progressValue} of ${progressMax} factors analyzed`}>
              {/* Background Bar */}
              <div className="bg-[#151a20] opacity-50 rounded-[7.451px] h-[10px] w-full" />
              {/* Progress Fill */}
              <motion.div
                className="bg-[#afdfff] rounded-[7.451px] h-[10px] relative -mt-[10px]"
                initial={{ width: 0 }}
                animate={{ width: `${progressPercentage}%` }}
                transition={{ duration: 0.8, ease: "easeOut" }}
              />
            </div>

            {/* Progress Text */}
            <div className="flex gap-[10px] items-center w-full">
              <p className="font-bold text-[#183344] text-[12px] leading-[1.3]" aria-hidden="true">
                {progressValue}/{progressMax} Factors Analyzed
              </p>
            </div>
          </div>
        </div>

        {/* Description and Action Section */}
        <div className="flex flex-col w-full gap-[24px] items-center flex-1">
          <div className="flex gap-[10px] items-center w-full justify-center">
            <div className="font-normal text-[#183344] text-[14px] leading-[1.4] w-full text-left">
              <PortableText content={description} />
            </div>
          </div>

          {/* Custom content slot (buttons, expanded content, etc.) */}
          {children}
        </div>
      </div>
    </div>
  );
});

export default CategoryCardBase;
